import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SeoHead from './SeoHead';
import NewsletterSignup from './NewsletterSignup';
import { journalPosts, allCategories } from '../data/journal';

export default function Journal() {
  const [featured, ...rest] = journalPosts;
  const categories = allCategories();

  // Blog + ItemList schema — establishes /insights as a proper Blog entity
  // and lists every post so Google can surface them as a content hub.
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': 'https://smgmediagroup.com/insights',
    name: 'SMG Media Group Journal',
    description: 'Long-form thinking from SMG Media Group on the AI + culture + revenue operating layer.',
    url: 'https://smgmediagroup.com/insights',
    publisher: {
      '@type': 'Organization',
      name: 'SMG Media Group',
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/jhtdaos4_SMG%20ALL%20LOGO%20PNG.png',
      },
    },
    blogPost: journalPosts.map((p) => ({
      '@type': 'BlogPosting',
      '@id': `https://smgmediagroup.com/insights/${p.slug}`,
      headline: p.title,
      description: p.dek,
      image: p.heroImage,
      url: `https://smgmediagroup.com/insights/${p.slug}`,
      articleSection: (p.categories || []).map((c) => c.name),
      author: { '@type': 'Organization', name: p.author || 'SMG Editorial' },
    })),
  };

  return (
    <>
      <SeoHead
        title="Journal — SMG Media Group POV on AI, Culture & Brand Growth"
        description="Field notes and long-form essays from SMG Media Group on the AI + culture + revenue operating layer. Written for CMOs, founders, and growth teams."
        path="/insights"
        jsonLd={[blogSchema]}
      />
      <Header />
      <main className="pt-32 md:pt-40 pb-24 md:pb-32 px-5 md:px-10 lg:px-14" data-testid="journal-index">
        {/* Kicker */}
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] tracking-[0.4em] text-white/50 mb-6">— THE JOURNAL</p>
          <h1 className="font-display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-6">
            Field notes on brands,<br />
            culture, and the AI layer<br />
            underneath both.
          </h1>
          <p className="text-white/60 max-w-2xl text-base md:text-lg leading-relaxed">
            Long-form thinking from inside SMG. Written for the CMOs, founders, and growth teams operating at the intersection of intelligence and intuition.
          </p>
        </div>

        {/* Featured post */}
        {featured && (
          <Link
            to={`/insights/${featured.slug}`}
            className="block max-w-7xl mx-auto mt-16 md:mt-24 group"
            data-testid={`journal-featured-${featured.slug}`}
          >
            <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
              <div className="md:col-span-7 relative overflow-hidden aspect-[16/10] bg-neutral-900">
                <img
                  src={featured.heroImage}
                  alt=""
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${featured.accent}33 0%, rgba(0,0,0,0.45) 60%, transparent 100%)` }} />
                <span className="absolute top-5 left-5 text-[10px] tracking-[0.4em] text-white/90 bg-black/40 backdrop-blur px-3 py-1.5">FEATURED</span>
              </div>
              <div className="md:col-span-5">
                <p className="text-[11px] tracking-[0.4em] mb-4" style={{ color: featured.accent }}>{featured.kicker}</p>
                <h2 className="font-display text-white text-3xl md:text-4xl lg:text-5xl leading-[1] tracking-tight mb-4 group-hover:opacity-80 transition-opacity">
                  {featured.title}
                </h2>
                <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">{featured.dek}</p>
                <div className="flex items-center gap-6 text-[11px] tracking-[0.3em] text-white/50">
                  <span>{featured.date}</span>
                  <span>{featured.readTime}</span>
                </div>
                <span className="inline-flex items-center gap-2 mt-8 text-[11px] tracking-[0.3em] text-white group-hover:text-[#FF3D2E] transition-colors">
                  READ THE FULL ESSAY <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Divider */}
        <div className="max-w-7xl mx-auto mt-20 md:mt-28 mb-6 md:mb-8 flex items-baseline justify-between">
          <p className="text-[11px] tracking-[0.4em] text-white/50">— MORE ESSAYS</p>
          <span className="text-[11px] tracking-[0.3em] text-white/30">{rest.length} POSTS</span>
        </div>

        {/* Category chips */}
        <div className="max-w-7xl mx-auto mb-10 md:mb-14 flex flex-wrap gap-2" data-testid="journal-category-chips">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/insights/category/${c.slug}`}
              className="text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border border-white/15 text-white/55 hover:border-white/40 hover:text-white transition-colors"
              data-testid={`journal-chip-${c.slug}`}
            >
              {c.name} <span className="text-white/30">· {c.count}</span>
            </Link>
          ))}
        </div>

        {/* Grid */}
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12">
          {rest.map((post) => (
            <Link
              key={post.slug}
              to={`/insights/${post.slug}`}
              className="group block"
              data-testid={`journal-card-${post.slug}`}
            >
              <div className="relative overflow-hidden aspect-[16/10] bg-neutral-900 mb-6">
                <img
                  src={post.heroImage}
                  alt=""
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${post.accent}33 0%, rgba(0,0,0,0.5) 55%, transparent 100%)` }} />
                <div className="absolute bottom-0 left-0 right-0 h-[3px]" style={{ background: post.accent }} />
              </div>
              <p className="text-[10px] tracking-[0.4em] mb-3" style={{ color: post.accent }}>{post.kicker}</p>
              <h3 className="font-display text-white text-2xl md:text-3xl leading-[1.05] tracking-tight mb-3 group-hover:opacity-80 transition-opacity">
                {post.title}
              </h3>
              <p className="text-white/60 text-sm md:text-base leading-relaxed mb-4">{post.dek}</p>
              <div className="flex items-center gap-5 text-[11px] tracking-[0.3em] text-white/40">
                <span>{post.date}</span>
                <span>·</span>
                <span>{post.readTime}</span>
              </div>
            </Link>
          ))}
        </div>
        {/* Newsletter signup — end of grid */}
        <div className="max-w-7xl mx-auto mt-20 md:mt-28 border-t border-white/10 pt-14 md:pt-20 grid md:grid-cols-12 gap-8 md:gap-14 items-start">
          <div className="md:col-span-6">
            <p className="text-[11px] tracking-[0.4em] text-[#FF3D2E] mb-4">— FIELD NOTES</p>
            <h2 className="font-display text-white text-4xl md:text-5xl leading-[0.98] tracking-tight">
              Thursday morning<br />in your inbox.
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mt-5 max-w-md">
              Five things every week from the studio floor: one cultural signal · one co-brand to watch · one framework · one SMG update · one provocation.
            </p>
          </div>
          <div className="md:col-span-6 md:pl-10">
            <NewsletterSignup source="journal-footer" />
            <p className="mt-4 text-[11px] tracking-[0.2em] text-white/40">FIRST DROP · THURSDAY · 8AM · UNSUBSCRIBE ANY TIME</p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
